import React, { useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Circle,
  InfoWindow,
} from "@react-google-maps/api";
import { getAddressByLatLng } from "./MapFunction";
import { ImOffice } from "react-icons/im";
import { renderToStaticMarkup } from "react-dom/server";
import { useSelector } from "react-redux";

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY;

/* ── Never pass NaN to Google Maps ─────────────────────────────── */
const DEFAULT_CENTER = { lat: 12.985908334627606, lng: 77.58141163898391 };

const safeFloat = (v) => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

const isValidLatLng = (c) =>
  c != null &&
  typeof c.lat === "number" && Number.isFinite(c.lat) &&
  typeof c.lng === "number" && Number.isFinite(c.lng);

const darkModeStyle = [
  { elementType: "geometry",            stylers: [{ color: "#010113" }] },
  { elementType: "labels.icon",         stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill",    stylers: [{ color: "#757575" }] },
  { elementType: "labels.text.stroke",  stylers: [{ color: "#212121" }] },
  { featureType: "administrative", elementType: "geometry",         stylers: [{ color: "#757575" }] },
  { featureType: "poi",            elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road",           elementType: "geometry",         stylers: [{ color: "#383838" }] },
  { featureType: "water",          elementType: "geometry",         stylers: [{ color: "#000000" }] },
  { featureType: "water",          elementType: "labels.text.fill", stylers: [{ color: "#3d3d3d" }] },
];

const Map = ({
  center,
  radius = 1000,
  lat,
  lng,
  name,
  isMapOnly = false,
  zoom = 15,
  width = "100%",
  height = "100%",
}) => {
  const themeStore = useSelector((state) => state.theme);
  const iconSVG = renderToStaticMarkup(<ImOffice size={24} color="#FF5722" />);

  const [branchAddresses, setBranchAddresses] = useState("");
  const [branchAddress,   setBranchAddress]   = useState("");
  const [mapLoaded,       setMapLoaded]       = useState(false);
  const [map,             setMap]             = React.useState(null);

  const containerStyle = { width, height };

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: API_KEY,
  });

  const onLoad    = React.useCallback((m) => { setMap(m); setMapLoaded(true);  }, []);
  const onUnmount = React.useCallback(() =>  { setMap(null); setMapLoaded(false); }, []);

  /* ── derive safe coords once — used everywhere below ── */
  const markerLat = safeFloat(lat);
  const markerLng = safeFloat(lng);
  /* markerPos is null when coords are missing/invalid — gates ALL child renders */
  const markerPos = (markerLat !== null && markerLng !== null)
    ? { lat: markerLat, lng: markerLng }
    : null;

  /* Map center: validated prop → marker coords → Bangalore fallback */
  const mapCenter = isValidLatLng(center)
    ? center
    : (markerPos ?? DEFAULT_CENTER);

  /* Safe radius — Circle crashes on null/0/NaN */
  const safeRadius = (() => {
    const r = parseInt(radius, 10);
    return Number.isFinite(r) && r > 0 ? r : 1000;
  })();

  const getBranchAddress = async (pos) => {
    setBranchAddress(name);
    setBranchAddresses(await getAddressByLatLng(pos));
  };
  const resetBranchAddress = () => setBranchAddress("");

  const mapOptions = {
    streetViewControl: false,
    mapTypeControl: false,
    minZoom: 5,
    zoomControl: true,
    zoomControlOptions:       { position: 6 },
    fullscreenControl: true,
    fullscreenControlOptions: { position: 1 },
    disableDefaultUI: true,
  };

  return isLoaded ? (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={mapCenter}          /* always a valid {lat,lng} — never NaN */
      zoom={zoom}
      onLoad={onLoad}
      onUnmount={onUnmount}
      options={{
        styles: themeStore.theme === "dark" ? darkModeStyle : null,
        ...mapOptions,
      }}
    >
      {/* Circle — only when map is ready AND coords are valid */}
      {mapLoaded && markerPos && (
        <Circle
          center={markerPos}
          radius={safeRadius}
          options={{
            strokeColor:   "#4B0082",
            strokeOpacity: 0.8,
            strokeWeight:  2,
            fillColor:     "#E6E6FA",
            fillOpacity:   0.35,
          }}
        />
      )}

      {/* Marker — only when coords are valid */}
      {markerPos && (
        isMapOnly ? (
          <Marker
            position={markerPos}
            onMouseOver={() => getBranchAddress(markerPos)}
            onMouseOut={resetBranchAddress}
          />
        ) : (
          <Marker
            position={markerPos}
            icon={{ url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(iconSVG)}` }}
            onMouseOver={() => getBranchAddress(markerPos)}
            onMouseOut={resetBranchAddress}
          />
        )
      )}

      {/* InfoWindow — only when address is set AND coords are valid */}
      {branchAddress && markerPos && (
        <InfoWindow
          position={markerPos}
          options={{ pixelOffset: new window.google.maps.Size(0, -25) }}
        >
          <div style={{ padding: "10px 10px 0", borderRadius: "10px", backgroundColor: "#fff", boxShadow: "0 4px 8px rgba(0,0,0,0.2)" }}>
            <h4 style={{ fontSize: "16px", color: "#333", textAlign: "center" }}>{branchAddress}</h4>
            <p  style={{ margin: 0,  fontSize: "14px", color: "#555", textAlign: "center" }}>{branchAddresses}</p>
          </div>
        </InfoWindow>
      )}
    </GoogleMap>
  ) : (
    <></>
  );
};

export default React.memo(Map);